import React, { useEffect, useRef, useState } from 'react'
import { Popup } from '@progress/kendo-react-popup'
import { SpinnerLoader } from '@/components/common/SpinnerLoader'
import { DelayedActionTooltip } from '@/components/common/DelayedActionTooltip'
import { cn } from '@/utils/mergeClassNames'
import ICON_MORE_VERTICAL from '@public/assets/common/icon_more_vertical.svg'
import COMMON_TOOLTIP_ICON from '@public/assets/icon_information.svg'

export interface MenuAction {
  accessibleLabel?: string
  enabled: boolean
  icon: string
  id: string
  loading?: boolean
  onClick: () => void | Promise<void>
  position?: 'left' | 'right' | 'top' | 'bottom'
  text: string
  tooltip?: string
}

interface ActionMenuProps {
  actions: MenuAction[]
  className?: string
  iconSize: number
  menuItemIconSize?: number
  menuItemGap?: number
  menuItemClassName?: string
  menuClassName?: string
  actionTooltipSize?: number
  renderActionTooltip?: (action: MenuAction) => React.ReactNode
  showActionTooltips?: boolean
  isMobile: boolean
  onOpenChange?: (open: boolean) => void
  closeRequestId?: number
  closeWhenAnchorHidden?: boolean
  forceMenu?: boolean
  triggerIcon?: string
}

function MenuActionTooltip({
  isMobile,
  message,
  position = 'left',
}: {
  isMobile: boolean
  message: string
  position?: 'left' | 'right' | 'top' | 'bottom'
}) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return undefined
    const close = (event: globalThis.MouseEvent) => {
      const target = event.target as Node
      if (
        anchorRef.current?.contains(target) ||
        popupRef.current?.contains(target)
      )
        return
      setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  if (!isMobile) {
    return (
      <DelayedActionTooltip
        label={message}
        delay={0}
        placement={position}
        wrap
        tooltipClassName="w-[264px] max-w-[264px] text-xs leading-4 tracking-[0.5px]"
      >
        <span
          className="inline-flex size-3.5 items-center justify-center"
          onClick={event => event.stopPropagation()}
        >
          <img
            alt=""
            className="block size-3.5"
            src={COMMON_TOOLTIP_ICON}
          />
        </span>
      </DelayedActionTooltip>
    )
  }

  return (
    <span
      ref={anchorRef}
      className="inline-flex size-3.5 cursor-pointer items-center justify-center"
      onClick={event => {
        event.preventDefault()
        event.stopPropagation()
        setOpen(current => !current)
      }}
    >
      <img alt="" className="block size-3.5" src={COMMON_TOOLTIP_ICON} />
      {open && (
        <Popup
          anchor={anchorRef.current}
          anchorAlign={{ horizontal: 'right', vertical: 'center' }}
          popupAlign={{ horizontal: 'left', vertical: 'center' }}
          show
          style={{ zIndex: 999999 }}
        >
          <div
            ref={popupRef}
            className="max-w-[220px] rounded bg-black px-2 py-1 text-xs font-normal leading-4 text-white"
            onClick={event => event.stopPropagation()}
          >
            {message}
          </div>
        </Popup>
      )}
    </span>
  )
}

export function ActionMenu({
  actions,
  className = '',
  iconSize,
  menuItemIconSize = 16,
  menuItemGap = 8,
  menuItemClassName = 'px-4 py-[5px] text-base leading-[22px] tracking-[0.5px]',
  menuClassName,
  actionTooltipSize = 16,
  renderActionTooltip,
  showActionTooltips = true,
  isMobile,
  onOpenChange,
  closeRequestId,
  closeWhenAnchorHidden = false,
  forceMenu = false,
  triggerIcon = ICON_MORE_VERTICAL,
}: ActionMenuProps) {
  const anchorRef = useRef<HTMLImageElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const previousCloseRequestIdRef = useRef(closeRequestId)
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange

  useEffect(() => {
    onOpenChangeRef.current?.(open)
  }, [open])

  useEffect(() => {
    if (previousCloseRequestIdRef.current === closeRequestId) return
    previousCloseRequestIdRef.current = closeRequestId
    setOpen(false)
  }, [closeRequestId])
  const enabledActions = actions.filter(
    action => action.enabled || action.loading
  )

  useEffect(() => {
    if (!open) return undefined
    const close = (event: globalThis.MouseEvent) => {
      const target = event.target as Node
      if (
        anchorRef.current?.contains(target) ||
        popupRef.current?.contains(target)
      )
        return
      setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  useEffect(() => {
    if (!open || !closeWhenAnchorHidden) return undefined
    const anchor = anchorRef.current
    if (!anchor) return undefined
    // MIRA-4376: a portaled menu must close when its anchor leaves a clipped frame.
    const closeIfHidden = () => {
      const rect = anchor.getBoundingClientRect()
      let top = 0
      let bottom = window.innerHeight
      let left = 0
      let right = window.innerWidth
      for (let parent = anchor.parentElement; parent; parent = parent.parentElement) {
        const style = window.getComputedStyle(parent)
        const bounds = parent.getBoundingClientRect()
        if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
          top = Math.max(top, bounds.top)
          bottom = Math.min(bottom, bounds.bottom)
        }
        if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
          left = Math.max(left, bounds.left)
          right = Math.min(right, bounds.right)
        }
      }
      if (rect.bottom <= top || rect.top >= bottom || rect.right <= left || rect.left >= right) setOpen(false)
    }
    const observer = typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver(entries => {
          if (entries.some(entry => !entry.isIntersecting)) setOpen(false)
        })
    observer?.observe(anchor)
    document.addEventListener('scroll', closeIfHidden, true)
    window.addEventListener('resize', closeIfHidden)
    return () => {
      observer?.disconnect()
      document.removeEventListener('scroll', closeIfHidden, true)
      window.removeEventListener('resize', closeIfHidden)
    }
  }, [open, closeWhenAnchorHidden])

  if (enabledActions.length === 0) return null
  if (!forceMenu && actions.length === 1) {
    const action = actions[0]
    return (
      <button
        className={`${className} relative inline-flex items-center border-0 bg-transparent p-0`}
        disabled={action.loading}
        aria-busy={action.loading}
        aria-label={action.text}
        onClick={event => {
          event.stopPropagation()
          void action.onClick()
        }}
        type="button"
      >
        {action.loading ? (
          <span
            className="inline-flex items-center justify-center"
            style={{ height: iconSize, width: iconSize }}
          >
            <SpinnerLoader label="" size={Math.max(iconSize, 18)} />
          </span>
        ) : (
          <img
            alt={action.text}
            height={iconSize}
            src={action.icon}
            width={iconSize}
          />
        )}
      </button>
    )
  }

  return (
    <span className={`${className} relative inline-flex`}>
      <img
        ref={anchorRef}
        alt="More actions"
        role="button"
        aria-label="More actions"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            event.stopPropagation()
            setOpen(current => !current)
          }
        }}
        className="cursor-pointer"
        height={iconSize}
        src={triggerIcon}
        width={iconSize}
        onClick={event => {
          event.stopPropagation()
          setOpen(current => !current)
        }}
      />
      <Popup
        anchor={anchorRef.current}
        anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
        collision={{ horizontal: 'flip', vertical: 'flip' }}
        popupAlign={{ horizontal: 'right', vertical: 'top' }}
        show={open}
        popupClass="mira-dropdown"
        style={{ transform: 'translateX(15px) translateY(10px)' }}
      >
        <div
          ref={popupRef}
          className={cn('k-group border-0 bg-white text-base font-normal', menuClassName)}
          onClick={event => event.stopPropagation()}
        >
          {actions.map(action => (
            <div
              key={action.id}
              aria-label={action.accessibleLabel}
              aria-disabled={!action.enabled}
              style={{
                gridTemplateColumns: `${menuItemIconSize}px minmax(0, 1fr)${showActionTooltips ? ` ${actionTooltipSize}px` : ''}`,
                columnGap: menuItemGap,
              }}
              className={`mira-dropdown-item grid min-h-7 w-full select-none items-center whitespace-nowrap border-0 bg-transparent text-left ${menuItemClassName} ${
                action.enabled
                  ? 'cursor-pointer text-[#333]'
                  : 'cursor-not-allowed text-[#9e9e9e]'
              }`}
              onClick={() => {
                if (!action.enabled) return
                setOpen(false)
                void action.onClick()
              }}
              role="menuitem"
              aria-busy={action.loading}
            >
              {action.loading ? (
                <span
                  className="inline-flex shrink-0 items-center justify-center"
                  style={{ width: menuItemIconSize, height: menuItemIconSize }}
                >
                  <SpinnerLoader
                    label=""
                    size={Math.max(menuItemIconSize, 18)}
                  />
                </span>
              ) : (
                <img
                  alt=""
                  className={`block shrink-0 ${action.enabled ? '' : 'opacity-60'}`}
                  width={menuItemIconSize}
                  height={menuItemIconSize}
                  src={action.icon}
                />
              )}
              <span className="inline-flex min-h-4 items-center">
                {action.text}
              </span>
              {showActionTooltips &&
                (renderActionTooltip ? renderActionTooltip(action) : action.tooltip ? (
                  <MenuActionTooltip
                    isMobile={isMobile}
                    message={action.tooltip}
                    position={action.position}
                  />
                ) : (
                  <span style={{ width: actionTooltipSize, height: actionTooltipSize }} />
                ))}
            </div>
          ))}
        </div>
      </Popup>
    </span>
  )
}
